/** **************************************************************************************
 * Excel exporter – Funding-Desk application + KYC client info
 *  • KYC Client Info (incl. Summary, Farm profile, Funding Ask)
 *  • Financial Overview & Cash-flow (production plans, seed / fert / chem costs injected
 *    into the planting-month, pivoted historic financials, production history)
 *  • Liabilities & Assets
 *  • People & Partners
 *  • Documents
 ****************************************************************************************/
import ExcelJS from 'exceljs';
import {saveAs} from 'file-saver';
import {
  IFdApplicationType,
  ITraderApplication,
  ISupplierCompanyProfile,
} from 'constants/interfaces';
import {lshParseOperatingCostMonthlyCosts} from '../FinancialInformationTab/utils/operatingCostMonthlyCosts';

/* ───────── reusable style helpers ───────── */
const randFmt = '"R" #,##0';

const headerFill: ExcelJS.FillPattern = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: {argb: 'FF267373'},
};
const totalFill: ExcelJS.FillPattern = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: {argb: 'FFE2E2E2'},
};

const thinBorder: Partial<ExcelJS.Borders> = {
  top: {style: 'thin'},
  left: {style: 'thin'},
  bottom: {style: 'thin'},
  right: {style: 'thin'},
};

const styleHeader = (row: ExcelJS.Row): void => {
  row.font = {bold: true, size: 12, color: {argb: 'FFFFFFFF'}};
  row.eachCell({includeEmpty: false}, (c) => {
    c.fill = headerFill;
  });
};

const styleTotal = (row: ExcelJS.Row): void => {
  row.font = {bold: true, size: 11};
  row.eachCell({includeEmpty: false}, (c) => {
    c.fill = totalFill;
  });
};

const putRand = (cell: ExcelJS.Cell, v: number | null | undefined): void => {
  if (typeof v === 'number') {
    cell.value = v;
    cell.numFmt = randFmt;
  } else {
    cell.value = v ?? '';
  }
};

const title = (ws: ExcelJS.Worksheet, txt: string): void => {
  const r = ws.addRow([txt]);
  r.font = {bold: true, size: 14};
};

const addBorders = (ws: ExcelJS.Worksheet): void =>
  ws.eachRow({includeEmpty: false}, (row) =>
    row.eachCell({includeEmpty: false}, (c) => {
      c.border = thinBorder;
    }),
  );

const autoWidth = (ws: ExcelJS.Worksheet): void =>
  ws.columns.forEach((col) => {
    if (!col) return;
    let max = 6;
    col.eachCell?.({includeEmpty: false}, (c) => {
      max = Math.max(max, String(c.value ?? '').length);
    });
    col.width = max + 2;
  });

/* ───────── misc helpers ───────── */
const fullAddress = (c?: ISupplierCompanyProfile) =>
  c
    ? [c.addressLine1, c.addressLine2, c.city, c.province]
        .filter(Boolean)
        .join(', ')
    : '';

const getCompanyRegistrationNumber = (tr?: ITraderApplication): string => {
  if (!tr?.forms) return '';
  const regField = tr.forms
    .flatMap((f) => f.sections)
    .flatMap((s) => s.fields)
    .find(
      (f) =>
        f.id ===
        'private_company_form_company_verification_company_registration_number',
    );
  return (regField as any)?.value ?? '';
};

/* ───────── related parties helper (now grabs ID & address correctly) ───────── */
type Related = {
  fullName?: string;
  idNumber?: string;
  phone?: string;
  address?: string;
};

const relatedParties = (tr?: ITraderApplication): Related[] => {
  if (!tr?.forms) return [];

  const fields = tr.forms
    .flatMap((f) => f.sections)
    .flatMap((s) => s.fields)
    .filter((f) => f.id?.startsWith('private_company_form_related_parties_'));

  const grouped = new Map<string, Partial<Related>>();

  fields.forEach((f) => {
    const id = f.id ?? '';
    const keyMatch = id.match(/related_parties_(\d+)_/);
    const key = keyMatch ? keyMatch[1] : 'root';
    const rec = grouped.get(key) ?? {};

    if (id.endsWith('_title')) rec.fullName = (f as any).title;
    else if (id.endsWith('_id_number')) rec.idNumber = (f as any).value;
    else if (id.endsWith('_phone')) rec.phone = (f as any).value;
    else if (id.endsWith('_address_line_1')) rec.address = (f as any).value;

    grouped.set(key, rec);
  });

  const merged: Record<string, Related> = {};
  [...grouped.values()].forEach((p) => {
    const handle = p.idNumber || p.fullName;
    if (!handle) return;
    merged[handle] = {...merged[handle], ...p};
  });

  return Object.values(merged);
};

/* ======================================================================== */
export async function exportFdApplicationToExcel(
  app: IFdApplicationType,
  traderApp?: ITraderApplication,
) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Khula Funding Desk';
  wb.created = new Date();

  /* ─────────────── 1. KYC CLIENT INFO ─────────────── */
  const kyc = wb.addWorksheet('KYC Client Info');
  const addK = (...v: any[]) => {
    const r = kyc.addRow(v);
    r.font = {size: 11};
    return r;
  };

  /* Summary */
  title(kyc, 'SUMMARY');
  styleHeader(addK('Field', 'Value'));
  [
    ['Application ID', app.id],
    ['State', app.applicationState],
    ['Created At', app.createdAt],
    ['Progress (%)', app.progress],
    ['Completed', app.completed ? 'Yes' : 'No'],
    ['Applicant', `${app.applyingUser?.name} ${app.applyingUser?.surname}`],
    ['Email', app.applyingUser?.email],
    ['Phone', app.applyingUser?.phoneNumber],
    ['Credit Score', app.applyingUser?.creditScore],
  ].forEach((r) => addK(...r));
  addK();

  /* Farm profile */
  title(kyc, 'FARM PROFILE');
  styleHeader(addK('Field', 'Value'));
  [
    ['Name', app.farm?.name],
    ['Description', app.farm?.description],
    ['Address', app.farm?.address],
    ['Owned ha', app.farm?.ownedHectares],
    ['Leased ha', app.farm?.numberOfLeasedHectares],
    ['Lease End', app.farm?.leaseEndDate],
    ['Irrigated ha', app.farm?.irrigatedHectares],
    ['Dryland ha', app.farm?.drylandHectares],
    ['Farm arrangement', app.farm?.farmArrangement],
    ['Full-time farming', app.farm?.fullTimeFarming ? 'Yes' : 'No'],
    ['Borehole', app.farm?.borehole ? 'Yes' : 'No'],
    ['Dam', app.farm?.dam ? 'Yes' : 'No'],
    ['River', app.farm?.river ? 'Yes' : 'No'],
    ['Short-term goals', app.farm?.shortTermGoals],
    ['Long-term goals', app.farm?.longTermGoals],
    ['Land composition', app.farm?.landComposition],
    ['Workforce (#)', app.farm?.workforceNumber],
    ['Worker trust', app.farm?.workerTrust],
    ['Employment before & after', app.farm?.employmentBeforeAndAfter],
  ].forEach((r) => addK(...r));
  addK();

  /* Funding ask */
  title(kyc, 'FUNDING ASK');
  styleHeader(addK('Label', 'Cost'));
  const faStart = kyc.lastRow!.number + 1;
  (app.fundingAsk?.fundingRequestCosts ?? []).forEach((fc) => {
    const r = addK(fc.label, fc.cost);
    putRand(r.getCell(2), fc.cost);
  });
  const faTot = kyc.addRow([
    'TOTAL',
    {formula: `SUM(B${faStart}:B${kyc.lastRow!.number})`, result: 0},
  ]);
  styleTotal(faTot);
  faTot.getCell(2).numFmt = randFmt;

  if (app.fundingAsk?.notes) {
    addK();
    addK('Notes', app.fundingAsk.notes);
  }
  addK();

  /* Company & bank */
  const co = traderApp?.company;
  const companyRegNo =
    getCompanyRegistrationNumber(traderApp) ||
    (co as any)?.registrationNumber ||
    '';
  title(kyc, 'COMPANY DETAILS');
  styleHeader(addK('Field', 'Value'));
  [
    ['Registration name', co?.name],
    ['Trading name', co?.tradingName],
    ['Company registration no.', companyRegNo],
    ['VAT number', co?.vatNumber],
    ['Tax number', co?.taxNumber],
    ['Registered address', fullAddress(co)],
  ].forEach((r) => addK(...r));
  addK();

  title(kyc, 'BANK DETAILS');
  styleHeader(addK('Field', 'Value'));
  [
    ['Bank', co?.bank?.name],
    ['Branch code', co?.bankBranchCode],
    ['Account type', co?.bankAccountType?.name],
    ['Account no.', co?.bankAccountNumber],
  ].forEach((r) => addK(...r));
  addK();

  /* Related parties */
  title(kyc, 'RELATED PARTIES');
  styleHeader(addK('Full name', 'ID number', 'Phone', 'Address'));
  relatedParties(traderApp).forEach((p) =>
    addK(p.fullName ?? '', p.idNumber ?? '', p.phone ?? '', p.address ?? ''),
  );

  addBorders(kyc);
  autoWidth(kyc);

  /* ─────────────── 2. FINANCIAL OVERVIEW & Cash-flow ─────────────── */
  const fo = wb.addWorksheet('Financial Overview & Cash-flow', {
    views: [{state: 'frozen', ySplit: 1}],
  });
  const addF = (...v: any[]): ExcelJS.Row => {
    const r = fo.addRow(v);
    r.font = {size: 11};
    return r;
  };

  const colLetter = (n: number) =>
    String.fromCharCode('A'.charCodeAt(0) + n - 1);

  const seasonStart = new Date(2025, 7, 1);
  const NUM_MONTHS = 18;
  const seasonMonths = Array.from({length: NUM_MONTHS}, (_, i) => {
    const d = new Date(
      seasonStart.getFullYear(),
      seasonStart.getMonth() + i,
      1,
    );
    return (
      d.toLocaleString('en-ZA', {month: 'short'}) +
      '-' +
      String(d.getFullYear()).slice(-2)
    );
  });
  const seasonIdx = (d?: string | Date | null): number => {
    if (!d) return -1;
    const dt = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(+dt)) return -1;
    const diff =
      (dt.getFullYear() - seasonStart.getFullYear()) * 12 +
      (dt.getMonth() - seasonStart.getMonth());
    return diff >= 0 && diff < NUM_MONTHS ? diff : -1;
  };
  const colFromIdx = (idx: number) => 3 + idx;

  const greenFill: ExcelJS.FillPattern = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {argb: 'FFD1F2D1'},
  };
  const redFill: ExcelJS.FillPattern = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: {argb: 'FFF5C7C7'},
  };
  const greenCF: Partial<ExcelJS.Style> = {fill: greenFill};
  const redCF: Partial<ExcelJS.Style> = {fill: redFill};

  /* 2-A Production plans (with notes) */
  title(fo, 'PRODUCTION PLANS');
  styleHeader(
    addF(
      'Crop',
      'Hectares',
      'Yield (t/ha)',
      'Price',
      'Planting date',
      'Production type',
    ),
  );
  (app.productionPlans ?? []).forEach((p) => {
    const r = addF(
      p.crop?.name,
      p.numberOfHectares,
      p.estimatedYield,
      p.estimatedPrice,
      p.plantingDate ?? '',
      (p.irrigation ?? '').toUpperCase() === 'DRYLAND'
        ? 'Dryland'
        : 'Irrigated',
    );
    putRand(r.getCell(4), p.estimatedPrice);

    if (p.notes) {
      addF('', 'Notes', p.notes);
    }
  });
  addF();

  /* 2-B Cash-flow header */
  title(fo, 'OPERATING COSTS (Cash-flow)');
  styleHeader(addF('Year', 'Item', ...seasonMonths, 'Total'));

  const revenueStart = fo.lastRow!.number + 1;

  /* 2-C-1 Revenue from crop sales */
  (app.productionPlans ?? []).forEach((plan) => {
    if (!plan.crop?.name) return;
    const revenue =
      (plan.numberOfHectares || 0) *
      (plan.estimatedYield || 0) *
      (plan.estimatedPrice || 0);
    if (!revenue) return;

    const harvest = plan.plantingDate
      ? new Date(
          new Date(plan.plantingDate).setMonth(
            new Date(plan.plantingDate).getMonth() + 9,
          ),
        )
      : undefined;

    const idx = seasonIdx(harvest);
    if (idx === -1) return;

    const r = addF(
      harvest!.getFullYear(),
      `${plan.crop.name} Revenue`,
      ...Array(NUM_MONTHS).fill(''),
      '',
    );
    putRand(r.getCell(colFromIdx(idx)), revenue);
    putRand(r.getCell(3 + NUM_MONTHS), revenue);
  });

  /* 2-C-2 Funding ask draw-down */
  const fundingTotal = (app.fundingAsk?.fundingRequestCosts ?? []).reduce(
    (s, c) => s + (c?.cost || 0),
    0,
  );
  if (fundingTotal) {
    const r = addF(
      seasonStart.getFullYear(),
      'Funding Ask Draw-down',
      ...Array(NUM_MONTHS).fill(''),
      '',
    );
    putRand(r.getCell(colFromIdx(0)), fundingTotal);
    putRand(r.getCell(3 + NUM_MONTHS), fundingTotal);
  }

  /* TOTAL revenue */
  const revenueEnd = fo.lastRow!.number;
  const revTotRow = addF(
    '',
    'TOTAL REVENUE',
    ...Array(NUM_MONTHS).fill(''),
    '',
  );
  styleTotal(revTotRow);
  revTotRow.eachCell((c, n) => {
    if (n >= 3 && n <= 2 + NUM_MONTHS) c.fill = greenFill;
  });
  for (let i = 0; i < NUM_MONTHS; i++) {
    const col = colLetter(colFromIdx(i));
    revTotRow.getCell(colFromIdx(i)).value = {
      formula: `SUM(${col}${revenueStart}:${col}${revenueEnd})`,
      result: 0,
    };
    revTotRow.getCell(colFromIdx(i)).numFmt = randFmt;
  }
  revTotRow.getCell(3 + NUM_MONTHS).value = {
    formula: `SUM(${colLetter(3 + NUM_MONTHS)}${revenueStart}:${colLetter(
      3 + NUM_MONTHS,
    )}${revenueEnd})`,
    result: 0,
  };
  revTotRow.getCell(3 + NUM_MONTHS).numFmt = randFmt;
  addF();

  /* TOTAL costs placeholder */
  const costTotRow = addF('', 'TOTAL COSTS', ...Array(NUM_MONTHS).fill(''), '');
  styleTotal(costTotRow);
  costTotRow.eachCell((c, n) => {
    if (n >= 3 && n <= 2 + NUM_MONTHS) c.fill = redFill;
  });
  const costStart = fo.lastRow!.number + 1;
  let costEnd = costStart - 1;

  const idxByCrop = new Map<string, number>();
  (app.productionPlans ?? []).forEach((p) => {
    const idx = seasonIdx(p.plantingDate);
    if (p.crop?.id && idx !== -1) idxByCrop.set(p.crop.id, idx);
  });

  /* 2-D Seed/Fert/Chem costs (+notes) */
  (app.productionCosts ?? []).forEach((pc) => {
    const idx = idxByCrop.get(pc.crop?.id ?? '');
    if (idx === undefined) return;

    [
      {label: 'Seed', val: pc.seed},
      {label: 'Fertiliser', val: pc.fertiliser},
      {label: 'Chemical', val: pc.chemical},
    ].forEach(({label, val}) => {
      if (!val) return;
      const r = addF(
        pc.year,
        `${pc.crop?.name} ${label}`,
        ...Array(NUM_MONTHS).fill(''),
        '',
      );
      putRand(r.getCell(colFromIdx(idx)), val);
      putRand(r.getCell(3 + NUM_MONTHS), val);
      costEnd = r.number;
    });

    if (pc.notes) {
      const noteRow = addF(pc.year, `${pc.crop?.name} Notes`, pc.notes);
      noteRow.getCell(3 + NUM_MONTHS).value = '';
      costEnd = noteRow.number;
    }
  });

  /* 2-E Operating costs (+notes) */
  (app.operatingCosts ?? []).forEach((oc) => {
    const r = addF(
      oc.year,
      oc.expense?.name ?? 'Unknown',
      ...Array(NUM_MONTHS).fill(''),
      '',
    );
    let tot = 0;

    try {
      const normalised = lshParseOperatingCostMonthlyCosts(oc.monthlyCosts);
      if (Object.keys(normalised.monthlyValues).length > 0) {
        Object.entries(normalised.monthlyValues).forEach(([key, value]) => {
          const idx = seasonMonths.findIndex((m) =>
            m.toLowerCase().startsWith(key.slice(0, 3).toLowerCase()),
          );
          if (idx !== -1) {
            putRand(r.getCell(colFromIdx(idx)), value);
            tot += value;
          }
        });
      } else {
        const cfg = JSON.parse(oc.monthlyCosts as any);

        if (cfg.frequency === 'once-off') {
          const idx = seasonMonths.findIndex((m) =>
            m
              .toLowerCase()
              .startsWith((cfg.month || '').slice(0, 3).toLowerCase()),
          );
          if (idx !== -1) {
            const v = Number(cfg.total);
            putRand(r.getCell(colFromIdx(idx)), v);
            tot = v;
          }
        } else if (cfg.frequency === 'monthly') {
          (cfg.months ?? []).forEach((mName: string) => {
            const idx = seasonMonths.findIndex((m) =>
              m.toLowerCase().startsWith(mName.slice(0, 3).toLowerCase()),
            );
            if (idx !== -1) {
              const v = Number(cfg.total);
              putRand(r.getCell(colFromIdx(idx)), v);
              tot += v;
            }
          });
        } else if (cfg.frequency === 'quarterly') {
          const quarterMap: Record<string, string[]> = {
            Q1: ['Jan', 'Feb', 'Mar'],
            Q2: ['Apr', 'May', 'Jun'],
            Q3: ['Jul', 'Aug', 'Sep'],
            Q4: ['Oct', 'Nov', 'Dec'],
          };
          const quarters: string[] = cfg.quarters ?? [];
          const perMonth = Number(cfg.total) / 3;

          quarters.forEach((q) => {
            (quarterMap[q] ?? []).forEach((mName) => {
              const idx = seasonMonths.findIndex((m) =>
                m.toLowerCase().startsWith(mName.toLowerCase()),
              );
              if (idx !== -1) {
                putRand(r.getCell(colFromIdx(idx)), perMonth);
                tot += perMonth;
              }
            });
          });
        }
      }
    } catch {
      /* ignore */
    }
    putRand(r.getCell(3 + NUM_MONTHS), tot);
    costEnd = r.number;

    if (oc.notes) {
      const nRow = addF(
        oc.year,
        `${oc.expense?.name} Notes`,
        oc.notes,
        ...Array(NUM_MONTHS - 1).fill(''),
        '',
      );
      costEnd = nRow.number;
    }
  });
  addF();

  /* fill TOTAL costs */
  for (let i = 0; i < NUM_MONTHS; i++) {
    const col = colLetter(colFromIdx(i));
    costTotRow.getCell(colFromIdx(i)).value = {
      formula: `SUM(${col}${costStart}:${col}${costEnd})`,
      result: 0,
    };
    costTotRow.getCell(colFromIdx(i)).numFmt = randFmt;
  }
  costTotRow.getCell(3 + NUM_MONTHS).value = {
    formula: `SUM(${colLetter(3 + NUM_MONTHS)}${costStart}:${colLetter(
      3 + NUM_MONTHS,
    )}${costEnd})`,
    result: 0,
  };
  costTotRow.getCell(3 + NUM_MONTHS).numFmt = randFmt;

  /* NET & running balance */
  const netRow = addF('', 'NET (Rev-Cost)', ...Array(NUM_MONTHS).fill(''), '');
  styleTotal(netRow);
  for (let i = 0; i < NUM_MONTHS; i++) {
    const idx = colFromIdx(i);
    const col = colLetter(idx);
    netRow.getCell(idx).value = {
      formula: `${col}${revTotRow.number}-${col}${costTotRow.number}`,
      result: 0,
    };
    netRow.getCell(idx).numFmt = randFmt;
  }
  netRow.getCell(3 + NUM_MONTHS).value = {
    formula: `${colLetter(3 + NUM_MONTHS)}${revTotRow.number}-${colLetter(
      3 + NUM_MONTHS,
    )}${costTotRow.number}`,
    result: 0,
  };
  netRow.getCell(3 + NUM_MONTHS).numFmt = randFmt;

  const runRow = addF('', 'RUNNING BALANCE', ...Array(NUM_MONTHS).fill(''), '');
  styleTotal(runRow);
  runRow.getCell(3).value = {
    formula: `${colLetter(3)}${netRow.number}`,
    result: 0,
  };
  runRow.getCell(3).numFmt = randFmt;
  for (let i = 1; i < NUM_MONTHS; i++) {
    const idx = colFromIdx(i);
    runRow.getCell(idx).value = {
      formula: `${colLetter(idx)}${netRow.number}+${colLetter(idx - 1)}${
        runRow.number
      }`,
      result: 0,
    };
    runRow.getCell(idx).numFmt = randFmt;
  }
  runRow.getCell(3 + NUM_MONTHS).value = {
    formula: `SUM(${colLetter(3)}${runRow.number}:${colLetter(2 + NUM_MONTHS)}${
      runRow.number
    })`,
    result: 0,
  };
  runRow.getCell(3 + NUM_MONTHS).numFmt = randFmt;

  [netRow.number, runRow.number].forEach((rowNum) => {
    fo.addConditionalFormatting({
      ref: `C${rowNum}:${colLetter(2 + NUM_MONTHS)}${rowNum}`,
      rules: [
        {
          type: 'cellIs',
          operator: 'greaterThan',
          formulae: ['0'],
          style: greenCF,
          priority: 1,
        },
        {
          type: 'cellIs',
          operator: 'lessThan',
          formulae: ['0'],
          style: redCF,
          priority: 2,
        },
      ],
    });
  });

  /* 2-H Production history */
  title(fo, 'PRODUCTION HISTORY (ha / t)');
  const historyYears = [
    ...new Set((app.productionHistory ?? []).map((h) => h.year)),
  ].sort();
  styleHeader(
    addF('Crop', ...historyYears.flatMap((y) => [`${y} ha`, `${y} t/ha`])),
  );
  (
    [
      ...new Set((app.productionHistory ?? []).map((h) => h.crop?.name)),
    ] as string[]
  )
    .filter(Boolean)
    .forEach((crop) => {
      const row: (string | number)[] = [crop];
      historyYears.forEach((y) => {
        const rec = (app.productionHistory ?? []).find(
          (h) => h.crop?.name === crop && h.year === y,
        );
        row.push(rec?.numberOfHectaresFarmed ?? '');
        row.push(rec?.yieldPerHectare ?? '');
      });
      addF(...row);
    });
  addF();

  /* NEW 2-H-NOTES */
  title(fo, 'PRODUCTION HISTORY NOTES');
  styleHeader(addF('Year', 'Crop', 'Notes'));
  (app.productionHistory ?? [])
    .filter((h) => h.notes)
    .forEach((h) => addF(h.year, h.crop?.name, h.notes));
  addF();

  /* 2-I Historic financials */
  title(fo, 'HISTORIC FINANCIALS');
  const finYears = [
    ...new Set((app.financialPerformance ?? []).map((f) => f.year)),
  ].sort();
  styleHeader(addF('Metric', ...finYears));
  [
    {label: 'Revenue', key: 'revenue' as const},
    {label: 'Expenses', key: 'expenses' as const},
    {label: 'Operating Costs', key: 'operatingCosts' as const},
    {label: 'Profit', key: 'profit' as const},
  ].forEach(({label, key}) => {
    const r = addF(
      label,
      ...finYears.map((y) => {
        const fp = (app.financialPerformance ?? []).find((f) => f.year === y);
        return fp ? (fp as any)[key] : '';
      }),
    );
    finYears.forEach((_, i) =>
      putRand(r.getCell(i + 2), r.getCell(i + 2).value as number),
    );
  });

  title(fo, 'FINANCIAL NOTES');
  styleHeader(addF('Year', 'Notes'));
  finYears.forEach((y) => {
    const fp = (app.financialPerformance ?? []).find((f) => f.year === y);
    addF(y, fp?.notes ?? '');
  });

  addBorders(fo);
  autoWidth(fo);

  /* 3 Liabilities & Assets */
  const la = wb.addWorksheet('Liabilities & Assets');
  const laAdd = (...v: any[]) => {
    const r = la.addRow(v);
    r.font = {size: 11};
    return r;
  };
  title(la, 'LIABILITIES');
  styleHeader(
    laAdd('Lender', 'Limit', 'Balance', 'Installment', 'In Arrears', 'Type'),
  );
  (app.liabilities ?? []).forEach((l) => {
    const r = laAdd(
      l.lender,
      l.creditLimitAmount,
      l.currentBalanceLimit,
      l.monthlyInstallmentAmount,
      l.inArrears ? 'Yes' : 'No',
      l.liabilityAccountType?.name,
    );
    [2, 3, 4].forEach((i) =>
      putRand(r.getCell(i), r.getCell(i).value as number),
    );
  });
  la.addRow([]);
  title(la, 'ASSETS');
  styleHeader(
    laAdd('Description', 'Make', 'Model', 'Year', 'Condition', 'Value'),
  );
  (app.assets ?? []).forEach((a) => {
    const r = laAdd(
      a.description,
      a.make,
      a.model,
      a.year,
      a.condition,
      a.value,
    );
    putRand(r.getCell(6), a.value);
  });
  addBorders(la);
  autoWidth(la);

  /* 4 People & Partners */
  const pp = wb.addWorksheet('People & Partners');
  const addPP = (...v: any[]) => {
    const r = pp.addRow(v);
    r.font = {size: 11};
    return r;
  };
  title(pp, 'FARM TEAM');
  styleHeader(addPP('Name', 'Role', 'Description'));
  (app.team ?? []).forEach((t) =>
    addPP(t.name, t.farmTeamRole?.name, t.farmTeamRole?.description),
  );
  pp.addRow([]);
  title(pp, 'CUSTOMERS');
  styleHeader(addPP('Crop', 'Customer'));
  (app.customers ?? []).forEach((c) => addPP(c.crop?.name, c.customer?.name));
  pp.addRow([]);
  title(pp, 'SUPPLIERS');
  styleHeader(addPP('Supplier', 'Product'));
  (app.suppliers ?? []).forEach((s) => {
    const lists = !s.supplierList
      ? []
      : Array.isArray(s.supplierList)
      ? s.supplierList
      : [s.supplierList];
    lists.forEach((l) =>
      (l.products ?? []).forEach((p) => addPP(l.name, p.name)),
    );
  });
  pp.addRow([]);
  title(pp, 'RISKS & MITIGATIONS');
  styleHeader(addPP('Risk', 'Problem', 'Mitigation'));
  (app.farmRisk ?? []).forEach((rk) =>
    addPP(rk.risk?.name, rk.problem, rk.mitigation),
  );
  addBorders(pp);
  autoWidth(pp);

  /* 5 Documents */
  const docs = wb.addWorksheet('Documents');
  styleHeader(docs.addRow(['Type', 'URL']));
  (app.applicationDocuments ?? []).forEach((d) =>
    docs.addRow([d.documentType, d.filePath]),
  );
  docs.getColumn(2).width = 80;
  addBorders(docs);
  autoWidth(docs);

  /* save */
  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf]), `Application_${app.id}.xlsx`);
}
